import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, ShieldCheck, AlertTriangle, Check, Pause, Play, Ban } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const TYPE_LABEL = { foundation: "Foundation", shared: "Shared", signature: "Signature" };
const STATUS_STYLE = {
  active: "bg-[#5C8A72]/15 text-[#5C8A72]",
  paused: "bg-[#D98E73]/25 text-[#B5794E]",
  revoked: "bg-red-100 text-red-600",
  owner: "bg-[#144F75]/10 text-[#144F75]",
  none: "bg-[#F1EEE8] text-[#8FA89B]",
};

export default function ProviderRosterPanel() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["mgr-provider-roster"],
    queryFn: () => api.get("/manager/provider-roster").then((r) => r.data),
    refetchInterval: 30000,
  });
  const act = async (enhancer_id, service_id, action) => {
    try {
      const { data: res } = await api.post("/manager/service-approval", { enhancer_id, service_id, action });
      const label = { approve: "Certified", pause: "Paused", revoke: "Revoked" }[action];
      toast.success(res.appointments_flagged ? `${label} — ${res.appointments_flagged} appointment(s) flagged for review` : label);
      qc.invalidateQueries({ queryKey: ["mgr-provider-roster"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (isLoading) return <Loader2 className="animate-spin text-[#144F75]" />;
  const services = data?.services || [];
  return (
    <div data-testid="mgr-roster">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="text-[#144F75]" size={20} />
        <h2 className="font-serif text-2xl text-[#1A242B]">Certified provider roster</h2>
      </div>
      <p className="text-[#4A5D6B] text-sm mb-6">Certify, pause, or revoke each Enhancer per service at {data?.location_name || "your center"}. Only approved-roster Enhancers appear here. Pausing/revoking stops new bookings and flags upcoming appointments for review — records are kept.</p>
      {services.length === 0 ? (
        <p className="text-[#8FA89B]">No services are offered at this location yet.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {services.map((s) => (
            <div key={s.service_id} className="eyw-card p-5" data-testid={`roster-service-${s.service_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-serif text-lg text-[#1A242B] leading-tight">{s.title}</h3>
                  <span className="inline-block mt-1 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#144F75]/[0.08] text-[#144F75]">{TYPE_LABEL[s.service_type] || s.service_type}</span>
                </div>
                <span className="text-xs text-[#8FA89B] shrink-0">{s.provider_count} certified</span>
              </div>
              {s.provider_count === 0 && (
                <p className="mt-3 text-sm text-[#B5794E] flex items-center gap-1.5"><AlertTriangle size={14} /> No certified provider — bookings can't be fulfilled here.</p>
              )}
              <div className="mt-3 space-y-1.5">
                {s.providers.map((p) => (
                  <div key={p.enhancer_id} data-testid={`roster-provider-${s.service_id}-${p.enhancer_id}`} className="flex items-center justify-between gap-2 text-sm">
                    <span className="text-[#1A242B] flex items-center gap-2">
                      {p.name}
                      <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${STATUS_STYLE[p.status] || STATUS_STYLE.none}`}>{p.status}</span>
                    </span>
                    {p.status !== "owner" && (
                      <span className="flex items-center gap-1 shrink-0">
                        {(p.status === "none" || p.status === "revoked") && (
                          <button onClick={() => act(p.enhancer_id, s.service_id, "approve")} data-testid={`roster-approve-${s.service_id}-${p.enhancer_id}`} title="Certify" className="p-1.5 rounded-full text-[#5C8A72] hover:bg-[#5C8A72]/10"><Check size={15} /></button>
                        )}
                        {p.status === "active" && (
                          <button onClick={() => act(p.enhancer_id, s.service_id, "pause")} data-testid={`roster-pause-${s.service_id}-${p.enhancer_id}`} title="Pause" className="p-1.5 rounded-full text-[#B5794E] hover:bg-[#D98E73]/15"><Pause size={15} /></button>
                        )}
                        {p.status === "paused" && (
                          <button onClick={() => act(p.enhancer_id, s.service_id, "approve")} data-testid={`roster-restore-${s.service_id}-${p.enhancer_id}`} title="Restore" className="p-1.5 rounded-full text-[#5C8A72] hover:bg-[#5C8A72]/10"><Play size={15} /></button>
                        )}
                        {(p.status === "active" || p.status === "paused") && (
                          <button onClick={() => { if (window.confirm(`Revoke ${p.name} for ${s.title}? Records are kept; new bookings stop and upcoming appointments are flagged for review.`)) act(p.enhancer_id, s.service_id, "revoke"); }} data-testid={`roster-revoke-${s.service_id}-${p.enhancer_id}`} title="Revoke" className="p-1.5 rounded-full text-red-500 hover:bg-red-50"><Ban size={15} /></button>
                        )}
                      </span>
                    )}
                  </div>
                ))}
                {s.providers.length === 0 && <p className="text-xs text-[#8FA89B]">No roster Enhancers yet — invite them in the Enhancers tab.</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
